import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SelectValue } from 'app/interfaces/select-value.interface';
import { SelectBoxComponent } from './select-box/select-box.component';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        SelectBoxComponent,
        FormsModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectComponent implements OnChanges {
    @Input() small: boolean = false;
    @Input() label: string = '';
    @Input() placeholder: string = '';
    @Input() options: SelectValue[] = [];
    @Input() isClearable: boolean = true;
    @Input() selectedValue: SelectValue | null = null;
    @Input() required: boolean = false;
    @Input() disabled: boolean = false;
    @Input() searchable: boolean = true;
    @Input() loading: boolean = false;
    @Input() ghost: boolean = false;
    @Input() customBoxHeight?: string

    @Output() selectedValueChanged = new EventEmitter<SelectValue | null>();

    isOpen = false;
    dirty = false;
    searchTerm: string = '';
    filteredOptions: SelectValue[] = [];

    constructor(private cdr: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        // Si las opciones cambian, actualizar las opciones filtradas
        if (changes['options']) {
            this.searchTerm = ''
            this.filteredOptions = [...this.options];
            this.cdr.detectChanges();

            // Si hay un valor seleccionado, verificar que aún existe en las nuevas opciones
            if (this.selectedValue) {
                const stillExists = this.options.some(opt => opt.value === this.selectedValue?.value);
                if (!stillExists) {
                    this.onValueSelect(null);
                }
            }
        }

        // Si el valor seleccionado cambia externamente
        if (changes['selectedValue']) {
            this.cdr.detectChanges();
        }
    }

    toggleSelect(): void {
        if (!this.disabled) {
            this.isOpen = !this.isOpen;
            this.dirty = true;
            if (!this.isOpen) {
                this.searchTerm = '';
                this.filteredOptions = [...this.options];
            }
            this.cdr.detectChanges();
        }
    }

    onSearch(event: Event): void {
        const value = (event.target as HTMLInputElement).value.toLowerCase();
        this.searchTerm = value;
        this.filteredOptions = this.options.filter(option => 
            option.label.toLowerCase().includes(value)
        );
        this.cdr.detectChanges();
    }

    onValueSelect(value: SelectValue | null): void {
        if (!this.disabled) {
            this.isOpen = false;
            this.selectedValue = value;
            this.searchTerm = '';
            this.filteredOptions = [...this.options];
            this.selectedValueChanged.emit(value);
            this.cdr.detectChanges();
        }
    }

    onValueClear(event: MouseEvent): void {
        if (!this.disabled) {
            event.stopPropagation();
            this.onValueSelect(null);
        }
    }

    // Prevenir que el click en el input de búsqueda cierre el dropdown
    onSearchClick(event: MouseEvent): void {
        event.stopPropagation();
    }

    reset(): void {
        this.selectedValue = null;
        this.dirty = false;
        this.searchTerm = '';
        this.filteredOptions = [...this.options];
        this.cdr.detectChanges();
    }
}
